import { yupResolver } from '@hookform/resolvers/yup';
import { useStore } from 'framework7-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { store } from '@/config/store';
import { carSizes } from '@/consts/carWash';
import { Car, Prepaid } from '@/types/api/customerApi';
import { CarSize, CourseItem, OptionItem } from '@/types/api/reservationApi';
import { calculateCarSize } from '@/utils/carWash';

export const CLASS_NO_MAX_LENGTH = 3;
export const REGISTRY_NO_MAX_LENGTH = 4;
export const HIRAGANA_MAX_LENGTH = 1;

const validationSchema = yup.object().shape({
  car: yup.object().shape({
    size: yup.number().not([carSizes.UNKNOWN]).required(),
    tStockCarId: yup.number().when('unregisteredCar.useUserCar', {
      is: true,
      then: yup.number().required(),
    }),
    carwashItemCode: yup.number().required(),
    unregisteredCar: yup.object().shape({
      useUserCar: yup.boolean(),
      carVin: yup.string().when('useUserCar', {
        is: false,
        then: yup.string().required('車台番号は必須です'),
      }),
      modelCode: yup.string().when('useUserCar', {
        is: false,
        then: yup.string().required('型式は必須です'),
      }),
      transportName: yup.string().when('useUserCar', {
        is: false,
        then: yup.string().required('地域名は必須です'),
      }),
      classNo: yup.string().when('useUserCar', {
        is: false,
        then: yup
          .string()
          .required('分類番号は必須です')
          .max(
            CLASS_NO_MAX_LENGTH,
            `は${CLASS_NO_MAX_LENGTH}桁まで入力してください`,
          ),
      }),
      hiragana: yup.string().when('useUserCar', {
        is: false,
        then: yup
          .string()
          .required('平仮名等は必須です')
          .matches(/^([あ-ん])+$/, '全角ひらがなで入力してください')
          .max(
            HIRAGANA_MAX_LENGTH,
            `は${HIRAGANA_MAX_LENGTH}桁で入力してください`,
          ),
      }),
      registryNo: yup.string().when('useUserCar', {
        is: false,
        then: yup
          .string()
          .required('車両番号は必須です')
          .max(
            REGISTRY_NO_MAX_LENGTH,
            `は${REGISTRY_NO_MAX_LENGTH}桁まで入力してください`,
          ),
      }),
      makerName: yup.string().when('useUserCar', {
        is: false,
        then: yup.string().required('メーカーは必須です'),
      }),
      carName: yup.string().when('useUserCar', {
        is: false,
        then: yup.string().required('車種は必須です'),
      }),
      carColor: yup.string().when('useUserCar', {
        is: false,
        then: yup.string().required('車の色は必須です'),
      }),
    }),
  }),
});

export interface Step1FormData {
  car: {
    openSizeSelect: boolean;
    size?: CarSize;
    tStockCarId?: number;
    carwashItemCode?: number;
    optionItemCodes: number[];
    unregisteredCar: {
      useUserCar: boolean;
      carVin?: string;
      modelCode?: string;
      transportName?: string;
      classNo?: string;
      hiragana?: string;
      registryNo?: string;
      makerName?: string;
      carName?: string;
      carColor?: string;
    };
  };
}

export const useCarWashHandStep1 = (
  initialState: Step1FormData,
  courseItems: CourseItem[],
  optionItems: OptionItem[],
) => {
  const carsInfo = useStore(store, 'carsInfo') as Required<Car>[];
  const prepaid = useStore(store, 'getFirstAdvancePaymentsInfo') as
    | Required<Prepaid>
    | undefined;
  const [paymentShort, setPaymentShort] = useState(0);
  const [fee, setFee] = useState(0);
  const [banner, setBanner] = useState<boolean>(false);
  const formMethods = useForm<Step1FormData>({
    defaultValues: initialState,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const { watch, setValue, trigger, reset } = formMethods;
  const carwashItemCode = watch('car.carwashItemCode');
  const optionItemCodes = watch('car.optionItemCodes');
  const useUserCar = watch('car.unregisteredCar.useUserCar');
  const tStockCarId = watch('car.tStockCarId');
  const openSizeSelect = watch('car.openSizeSelect');
  const size = watch('car.size');

  useEffect(() => {
    if (initialState.car.carwashItemCode) {
      reset(initialState);
    }
  }, [initialState, reset]);

  useEffect(() => {
    const coursePrice = courseItems.find((c) => c.code === carwashItemCode);
    const optionPrice = optionItems.find((c) =>
      optionItemCodes.includes(c.code),
    );
    const discountInfo = courseItems.some(
      (item) => !!item.discount_info?.length,
    );
    setBanner(discountInfo);
    // if (coursePrice && coursePrice.car_size !== size) {
    //   setValue('car.carwashItemCode', undefined);
    //   trigger('car.carwashItemCode');
    // }
    // if (optionPrice && optionPrice.car_size !== size) {
    //   setValue('car.optionItemCodes', []);
    //   trigger('car.optionItemCodes');
    // }
  }, [
    size,
    carwashItemCode,
    optionItemCodes,
    courseItems,
    optionItems,
    setValue,
    trigger,
  ]);

  useEffect(() => {
    if (useUserCar && tStockCarId && carsInfo) {
      const car = carsInfo.find((c) => c.t_stock_car_id === tStockCarId);
      if (car) {
        const fetchPredict = async () => {
          const predict = await calculateCarSize(car);

          setValue('car.size', predict);
          if (predict === carSizes.UNKNOWN) {
            setValue('car.openSizeSelect', true);
          } else {
            setValue('car.openSizeSelect', false);
          }
        };
        fetchPredict();
      }
    } else if (!openSizeSelect) {
      setValue('car.size', carSizes.UNKNOWN);
      setValue('car.openSizeSelect', !useUserCar);
    }
  }, [useUserCar, tStockCarId, carsInfo, openSizeSelect, setValue]);

  useEffect(() => {
    const course = courseItems.find((c) => c.code === carwashItemCode);
    const coursePrice =
      (!!course?.discount_info?.length
        ? course.discount_info[0].discounted_price
        : course?.price) || 0;
    const optionPrice = optionItems
      .filter((c) => optionItemCodes.includes(c.code))
      .reduce((current, item) => {
        const price = !!item?.discount_info?.length
          ? item.discount_info[0].discounted_price
          : item?.price;
        return current + price;
      }, 0);
    setFee(coursePrice + optionPrice);
    if (prepaid) {
      const shortage = Math.max(
        0,
        coursePrice + optionPrice - prepaid.balance_total,
      );
      setPaymentShort(shortage);
    }
  }, [carwashItemCode, courseItems, optionItemCodes, optionItems, prepaid]);

  return {
    fee,
    banner,
    prepaid,
    paymentShort,
    formMethods,
  };
};
